import type { ReactNode } from 'react';
import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { getColor, useTabbing } from '../../utils';
import type { Colors } from '../../utils/types';

// TIPS: put <Button /> in a <Box display="inline-box" /> container to make it inline for both desktop and mobile
// in order to suppress the build-in media-query

type ButtonProps = {
  /** Set `data-testid` for testing purpose */
  testId?: string;
  /** Depending on what you choose, `<Button/>` will change
   * its style to look like a normal `button` or `link`,
   * but it will still function like a button, see example in Storybook
   */
  styleAs?: 'button' | 'link';
  /** `style` is used in order to override the styles
   * of `<Button/>`, use this if needed
   */
  style?: object;
  /** In order to use the styled method on a Button */
  className?: string;
  /** Default set to `button`, use `submit`
   * when using `<Button/>` inside the `<Form/>` component as a submit button
   */
  type?: 'button' | 'submit';
  /** Changes the styles/variant of `<Button/>`,
   * choose correct `variant` depending on what Design has decided
   */
  variant?: 'primary' | 'outline' | 'transparent' | 'burgundy' | 'grey';
  /** Depending on what you choose, the `font-size` will be changed.
   * `medium` = 18px,
   * `standard` = 16px,
   * `small` = 14px (desktop), 16px tablet/mobile
   */
  fontSize?: 'small' | 'standard' | 'medium';
  /** `color` should only be used to override the text color of a `<Button />` */
  color?: Colors;

  /** Sets `<Button/>` as disabled */
  disabled?: boolean;
  /** Sets `<Button/>` to have 100% width (of it's parent width) */
  fullWidth?: boolean;
  /** Reset flex shrink and flex grow props if they cause unexpected behaviour */
  resetFlexProps?: boolean;
  /** Function to be triggered when `<Button/>` is clicked */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Text to display in `<Button/> */
  children: ReactNode;
  /** Button labels to screen readers */
  ariaLabel?: string;
};

const StyledButton = styled.button<ButtonProps & any>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  ${props =>
    !props.resetFlexProps &&
    css`
      flex-shrink: 0;
      flex-grow: 1;
    `}
  padding: 0rem 2.125rem;
  min-height: 3.125rem;
  color: ${props => getColor(props.color || 'white')};
  background-color: transparent;
  border: 0;
  border-radius: 2px;
  user-select: none;
  appearance: none;
  outline: none;
  cursor: pointer;

  ${props => props.fontSize === 'small' && theme.typography.textSmall}
  ${props => props.fontSize === 'standard' && theme.typography.textStandard}
  ${props => props.fontSize === 'medium' && theme.typography.textMedium}

  @media screen and (max-width: ${theme.breakpoint.lg - 1}px) {
    ${props =>
      props.styleAs !== 'link' &&
      css`
        min-height: 3.75rem;
      `}
  }
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    ${props =>
      props.styleAs !== 'link' &&
      css`
        ${props.fullWidth !== false && 'width: 100%'};
        font-size: ${props.fontSize === 'small' ? 1 : props.fontSize === 'medium' ? 1.125 : 1.25}rem;
      `}
  }
  ${props =>
    props.variant === 'grey' &&
    css`
      background-color: ${theme.colors.black05};
    `}
  ${props =>
    props.variant === 'primary' &&
    !props.disabled &&
    css`
      background-color: ${theme.colors.foraBlue};
      &:hover {
        background-color: ${theme.colors.darkBlue};
      }
      &:active {
        background-color: ${theme.colors.darkBlue};
        box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.3) inset;
      }
    `}
  ${props =>
    props.variant === 'primary' &&
    props.disabled &&
    css`
      color: ${theme.colors.black60};
      background-color: ${theme.colors.black20};
      border-bottom: 0;
    `}

  ${props =>
    props.variant === 'outline' &&
    !props.disabled &&
    css`
      color: ${props.color || theme.colors.foraBlue};
      background-color: ${theme.colors.white};
      border: 1px solid ${props.color || theme.colors.foraBlue};
      &:hover {
        box-shadow: 0px 0px 0px 1px ${props.color || theme.colors.foraBlue};
      }
      &:active {
        box-shadow: 0px 0px 0px 2px ${props.color || theme.colors.foraBlue};
      }
    `}

    ${props =>
    props.variant === 'outline' &&
    props.disabled &&
    css`
      color: ${theme.colors.black50};
      border: 1px solid ${theme.colors.black50};
    `}
  ${props =>
    props.variant === 'transparent' &&
    css`
      border: 1px solid ${theme.colors.white};
      &:hover {
        box-shadow: 0px 0px 0px 1px ${theme.colors.white};
      }
      &:active {
        box-shadow: 0px 0px 0px 2px ${theme.colors.white};
      }
    `}
  ${props =>
    props.variant === 'burgundy' &&
    !props.disabled &&
    css`
      background-color: ${theme.colors.burgundy};
      &:hover {
        background-color: ${theme.colors.burgundyDark};
      }
      &:active {
        background-color: ${theme.colors.burgundyDark};
        box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.3) inset;
      }
    `}

    ${props =>
    props.variant === 'burgundy' &&
    props.disabled &&
    css`
      color: ${theme.colors.black60};
      background-color: ${theme.colors.black20};
    `}

  ${props =>
    props.fullWidth &&
    css`
      width: 100% !important;
    `}


  ${props =>
    props.isTabbing &&
    css`
      &:focus {
        outline: ${props.variant === 'burgundy' ||
          props.variant === 'outline' ||
          (props.variant === 'transparent' && props.color === theme.colors.white)
            ? theme.colors.white
            : theme.colors.darkBlue}
          solid 2px;
        outline-offset: 5px;
      }
    `}

  &:disabled {
    cursor: not-allowed;
  }

  ${props =>
    props.styleAs === 'link' &&
    css`
      /* Use similar CSS-attributes from Link.tsx */
      min-height: auto;
      min-width: auto;
      background-color: transparent;
      padding: 0 0.125rem; /* in order to have some space between text and outline focus frame */
      border: none;
      ${props.variant === 'primary' && `color: ${theme.colors.foraBlue};`};
      ${props.variant === 'transparent' && `color: ${theme.colors.white};`};
      ${props.variant === 'burgundy' && `color: ${theme.colors.burgundy};`};
      ${props.disabled && `color: ${theme.colors.black50};`};
      ${props.color && `color: ${theme.colors[props.color]};`};
      &:hover,
      &:focus {
        box-shadow: none;
        text-decoration: underline;
        background-color: transparent;
      }
      ${props.isTabbing &&
      css`
        &:focus {
          outline: ${props.variant === 'transparent' ? theme.colors.white : theme.colors.black} solid 2px;
          outline-offset: 2px;
        }
      `}
    `}
`;

export const Button = ({
  testId,
  styleAs = 'button',
  type = 'button',
  variant = 'primary',
  fullWidth,
  fontSize = 'standard',
  color,
  ariaLabel,
  disabled = false,
  style,
  onClick,
  resetFlexProps = false,
  className,
  children,
}: ButtonProps) => {
  const isTabbing = useTabbing();

  return (
    <StyledButton
      data-testid={testId}
      role="button"
      styleAs={styleAs}
      type={type}
      variant={variant}
      fontSize={fontSize}
      color={color}
      disabled={disabled}
      style={style}
      fullWidth={fullWidth}
      onClick={onClick}
      isTabbing={isTabbing}
      className={className}
      resetFlexProps={resetFlexProps}
      aria-label={ariaLabel}
    >
      {children}
    </StyledButton>
  );
};
